import { z } from "zod";

import type { GpuSlug } from "./gpu.models";
import type { InferenceModelType } from "./models.model";

export const INFERENCE_ENGINES = ["vllm", "sglang", "trt", "mocked"] as const;

export const InferenceEngineSchema = z.enum(INFERENCE_ENGINES);

export type InferenceEngine = z.infer<typeof InferenceEngineSchema>;

export type EnvTag = "mainnet" | "tests" | "development";

// Define specific GPU config types for each engine
export type VllmGpuConfig = {
  gpus: readonly GpuSlug[];
  numberOfGpusRequiredToRunSingleInstanceOfModel: number;
  // Any flag is overridable here, including the per-instance defaults
  // (max-model-len, tensor-parallel-size, etc.). An operator-supplied value
  // always wins over the computed default for the same key.
  flagOverrides?: Partial<VllmFlags>;
  environmentVariableOverrides?: Partial<ConfigurableVllmEnvironmentVariables>;
  parallelism: number;
  maxNumberOfSequencesOverride?: number;
  averageTps: number | null;
  modelInitializationTimeoutMs?: number;
};

export type SglangGpuConfig = {
  gpus: readonly GpuSlug[];
  numberOfGpusRequiredToRunSingleInstanceOfModel: number;
  // Any flag is overridable here, including the per-instance defaults
  // (context-length, tp, etc.). An operator-supplied value always wins over
  // the computed default for the same key.
  flagOverrides?: Partial<SglangFlags>;
  environmentVariableOverrides?: Partial<ConfigurableSglangEnvironmentVariables>;
  parallelism: number;
  // AKA `max-running-requests`
  maxNumberOfSequencesOverride?: number;
  averageTps: number | null;
  modelInitializationTimeoutMs?: number;
};

export type TrtGpuConfig = {
  gpus: readonly GpuSlug[];
  numberOfGpusRequiredToRunSingleInstanceOfModel: number;
  // Any flag is overridable here, including the per-instance defaults
  // (max_seq_len, tp_size, etc.). An operator-supplied value always wins over
  // the computed default for the same key.
  flagOverrides?: Partial<TrtFlags>;
  configFiles?: {
    name: "trt-config";
    config: ConfigurableTrtConfigFileFlags;
    type: "yaml";
  }[];
  environmentVariableOverrides?: Partial<ConfigurableTrtEnvironmentVariables>;
  parallelism: number;
  maxNumberOfSequencesOverride?: number;
  averageTps: number | null;
  modelInitializationTimeoutMs?: number;
};

export type MockedModelConfig = {
  engine: "mocked";
  environmentsEnabled?: EnvTag[];
  environmentsEnabledWithModelOverride?: EnvTag[];
  flags: Partial<MockedFlags>;
  environmentVariables?: ConfigurableVllmEnvironmentVariables;
  gpuConfigs: VllmGpuConfig[];
};

// Define specific model config types for each engine
export type VllmModelConfig = {
  engine: "vllm";
  /**
   * Environments where this model config is enabled for automatic selection.
   */
  environmentsEnabled?: EnvTag[];
  /**
   * Additional environments where this model config can be used, but ONLY with
   * manual model override. This is a separate list from environmentsEnabled.
   *
   * This allows enabling models for testing in certain environments without
   * making them available for automatic selection by the system.
   *
   * For example, to enable a model on mainnet for automatic selection,
   * but allow testing it on development only with manual override:
   * - environmentsEnabled: ["mainnet"]
   * - environmentsEnabledWithModelOverride: ["development"]
   *
   * In this case, the model auto-selects on mainnet but requires explicit
   * override to run on development.
   */
  environmentsEnabledWithModelOverride?: EnvTag[];
  flags: Partial<VllmFlags>;
  environmentVariables?: ConfigurableVllmEnvironmentVariables;
  gpuConfigs: VllmGpuConfig[];
};

export type SglangModelConfig = {
  engine: "sglang";
  /**
   * Environments where this model config is enabled for automatic selection.
   */
  environmentsEnabled?: EnvTag[];
  /**
   * Additional environments where this model config can be used, but ONLY with
   * manual model override. This is a separate list from environmentsEnabled.
   *
   * This allows enabling models for testing in certain environments without
   * making them available for automatic selection by the system.
   *
   * For example, to enable a model on mainnet for automatic selection,
   * but allow testing it on development only with manual override:
   * - environmentsEnabled: ["mainnet"]
   * - environmentsEnabledWithModelOverride: ["development"]
   *
   * In this case, the model auto-selects on mainnet but requires explicit
   * override to run on development.
   */
  environmentsEnabledWithModelOverride?: EnvTag[];
  flags: Partial<SglangFlags>;
  environmentVariables?: ConfigurableSglangEnvironmentVariables;
  gpuConfigs: SglangGpuConfig[];
};

export type TrtModelConfig = {
  engine: "trt";
  environmentsEnabled?: EnvTag[];
  environmentsEnabledWithModelOverride?: EnvTag[];
  flags: Partial<TrtFlags>;
  environmentVariables?: ConfigurableTrtEnvironmentVariables;
  gpuConfigs: TrtGpuConfig[];
};

// Union type for all model configs
export type ModelConfig =
  | VllmModelConfig
  | SglangModelConfig
  | TrtModelConfig
  | MockedModelConfig;

/**
 * This is the resolved config that gets sent to the worker.
 * It combines the model config and the gpu config overrides.
 */
export type InferenceEngineConfig<Engine extends InferenceEngine> = {
  flags: Flags<Engine>;
  environmentVariables: EnvironmentVariables<Engine>;
  parallelism: number;
  engine: Engine;
  modelInitializationTimeoutMs: number;
  model: InferenceModelType;
  /** The L3 KV store the engine config selects; null when it runs no L3. */
  l3KvBackend: L3KvBackend | null;
  /**
   * The engine runs a team-owned model on a deployment no super admin
   * trusted. The worker then gives it only a minimal slice of its own env and
   * never lends it the platform Hugging Face token.
   */
  teamControlled: boolean;
  configFiles?: {
    name: string;
    config: Record<string, unknown>;
    type: "yaml" | "json";
  }[];
};

// Union type for all GPU configs
export type GpuConfig = VllmGpuConfig | SglangGpuConfig | TrtGpuConfig;

// Generic type for backwards compatibility
export type GenericGpuConfig<Engine extends InferenceEngine> =
  Engine extends "vllm"
    ? VllmGpuConfig
    : Engine extends "sglang"
      ? SglangGpuConfig
      : Engine extends "trt"
        ? TrtGpuConfig
        : never;

// ============= Flags =============

type Flags<Engine extends InferenceEngine> = Engine extends "vllm"
  ? VllmFlags
  : Engine extends "sglang"
    ? SglangFlags
    : Engine extends "trt"
      ? TrtFlags
      : Engine extends "mocked"
        ? MockedFlags
        : never;

export type VllmFlags = DefaultVllmFlags & ConfigurableVllmFlags;
export type SglangFlags = DefaultSglangFlags & ConfigurableSglangFlags;
export type TrtFlags = DefaultTrtFlags & ConfigurableTrtFlags;
export type MockedFlags = DefaultMockedFlags & ConfigurableMockedFlags;

export type DefaultVllmFlags = {
  port: string;
  host: string;

  "max-model-len": string;

  // Controlled through count and concurrency gpu config fields
  "max-num-seqs": string;
  "tensor-parallel-size": string;
  "data-parallel-size": string;
  // Boolean flag (no value) that makes vLLM surface prefix-cache hits in
  // usage.prompt_tokens_details.cached_tokens. On by default via the vLLM
  // engine flag floor; listed here so overrides are type-checked. The vLLM
  // analog of sglang's enable-cache-report (vLLM gates this behind
  // --enable-prompt-tokens-details, default FALSE in cli_args.py).
  "enable-prompt-tokens-details": "";
  "served-model-name"?: string;
};

export type DefaultSglangFlags = {
  port: string;
  host: string;

  // Controlled by model metadata
  "context-length": string;

  // Controlled through count and concurrency gpu config fields
  "max-running-requests": string;
  "cuda-graph-max-bs-decode": string;
  tp: string;
  "dp-size": string;
};

export type ConfigurableVllmFlags = Partial<{
  "gpu-memory-utilization": string;
  "max-seq-len-to-capture"?: string;
  "cuda-graph-sizes"?: string;
  "max-num-partial-prefills"?: string;
  "max-long-partial-prefills"?: string;
  "long-prefill-token-threshold"?: string;
  "block-size"?: string;
  // JSON object, e.g. {"method":"dspark","num_speculative_tokens":5}
  "speculative-config"?: string;

  // flag `--async-scheduling` no value
  "async-scheduling"?: string;

  // Overrides
  dtype: string;
  "num-gpu-blocks-override": string;
  device: string;

  // Tooling and reasoning
  "chat-template": string;
  "enable-auto-tool-choice": string;
  "tool-call-parser": string;
  "enable-reasoning": string;
  "reasoning-parser": string;

  "limit-mm-per-prompt": string;
  "max-num-batched-tokens": string;
  "scheduler-delay-factor": string;

  "tensor-parallel-size"?: string;
  "data-parallel-size"?: string;
  "disable-frontend-multiprocessing"?: string;
  quantization?: string;

  // KV cache quantization data type (e.g. "fp8")
  "kv-cache-dtype"?: string;

  // Additional flags from compose
  "trust-remote-code": string;
  "enable-chunked-prefill": string;
  "enable-prefix-caching": string;
  "no-enable-prefix-caching": string;
  // Disable the per-request uvicorn access log line (bare flag, empty value).
  "disable-uvicorn-access-log"?: string;
  // Disable vLLM request/stat logging bare flags.
  "no-enable-log-requests"?: string;
  "disable-log-stats"?: string;
  // Indicates we are starting the server in embedding mode
  task: "embed";

  // LoRA flags
  "enable-lora": string;
  "lora-modules": string;
  "max-lora-rank": string;
}>;

export const ONLINE_EVAL_UNSUPPORTED_VLLM_FLAGS = [
  // Server/frontend args the eval_serve runtime owns itself: it always serves on
  // 127.0.0.1, sets its own logging, and passes chat_template_kwargs per request,
  // so a serving-config value here would fight the library's authoritative one.
  // NOTE: enable-auto-tool-choice and tool-call-parser are deliberately NOT here
  // — the OpenAI server needs them to parse tool calls, and eval targets include
  // tool-call turns.
  "default-chat-template-kwargs",
  "disable-frontend-multiprocessing",
  "disable-log-stats",
  "disable-uvicorn-access-log",
  "host",
  "no-enable-log-requests",
  "port",

  // Not EngineArgs fields in vLLM 0.18.1, or owned by eval_serve (lora-modules is
  // built from the adapter path). Keep this list small so new engine args flow to
  // evals by default.
  "enable-reasoning",
  "grammar-backend",
  "lora-modules",
  "task",
] as const satisfies readonly (
  | keyof VllmFlags
  | "grammar-backend"
  | "default-chat-template-kwargs"
)[];

/**
 * Flags forced onto every online-eval engine, overriding the serving
 * config. Eval containers are ephemeral, so startup cost that a serving
 * pod amortizes over its lifetime is pure waste here: with the default
 * (flashinfer) backend, vLLM JIT-compiles the GDN prefill kernel for
 * ~12 minutes per container on GDN models (qwen3.5/qwen3-next) —
 * measured 829–874s of an ~17-minute eval on 2026-08-11. The triton
 * backend skips the JIT with a per-token prefill cost that is noise at
 * eval corpus sizes. A global EngineArgs field in vLLM 0.18.1; unused
 * by non-GDN models.
 */
export const ONLINE_EVAL_VLLM_FLAG_OVERRIDES = {
  "gdn-prefill-backend": "triton",
} as const;

export type OnlineEvalVllmFlags = Omit<
  Partial<VllmFlags>,
  (typeof ONLINE_EVAL_UNSUPPORTED_VLLM_FLAGS)[number]
> &
  Partial<Record<keyof typeof ONLINE_EVAL_VLLM_FLAG_OVERRIDES, string>>;

export type ConfigurableSglangFlags = Partial<{
  "mem-fraction-static": string;
  "schedule-conservativeness": string;

  // Tooling and templating
  "tool-call-parser": string;
  "chat-template": string;
  "grammar-backend": string;

  // Common flags
  dtype: string;
  "reasoning-parser": string;

  // Additional inference flags
  "enable-p2p-check": string;
  "speculative-algorithm": string;
  "speculative-draft-model-path": string;
  "speculative-num-steps": string;
  "speculative-eagle-topk": string;
  "speculative-num-draft-tokens": string;
  "enable-mixed-chunk": string;
  "chunked-prefill-size": string;
  "attention-backend": string;
  "trust-remote-code": string;

  // Hardware/runtime flags from compose
  "sgl-enable-jit-deepgemm": string;
  "model-initialization-timeout-in-ms": string;
  // Boolean flag (no value) that turns on embedding mode in sglang
  "is-embedding": "";
  // Boolean flag (no value) that makes sglang report prefix-cache hits in
  // usage.prompt_tokens_details.cached_tokens. On by default via the sglang
  // engine flag floor; listed here so overrides are type-checked.
  "enable-cache-report": "";
  // Boolean flag (no value) that exposes SGLang's Prometheus /metrics endpoint.
  "enable-metrics": "";
  // Boolean flag (no value) that turns on SGLang's host-RAM (L2) HiCache tier.
  "enable-hierarchical-cache": "";
  /**
   * SGLang's L3 HiCache store: the `file` store on the cluster-wide L3 volume
   * or the cluster's Mooncake store. Set only by the deployment resolver from
   * the engine config's `l3KvBackend`; engine configs and overrides never set
   * it. The worker CLI owns the store's location and env.
   */
  "hicache-storage-backend": L3KvBackend;
  /**
   * Boolean flag (no value) that turns on SGLang R-Fork for a deployment:
   * each engine lends its loaded weights over RDMA, and a new worker loads
   * them from a running one instead of from disk. The worker CLI adds the
   * transfer env and the load flags.
   */
  "remote-instance-weight-loader-start-seed-via-transfer-engine": "";

  /**
   * Tensor Parallelism Size
   *
   * Shards the model across multiple GPUs (each GPU will run a fraction of the model)
   */
  "tp-size": string;
  /**
   * Data Parallelism Size
   *
   * Replicates the model across multiple GPUs (the entire model will run on each GPU)
   */
  "dp-size": string;
}>;

/**
 * SGLang's storage-backend flag. Only the engine config's `l3KvBackend` sets it
 * (through the deployment resolver); engine configs and deployment overrides
 * that set it directly are rejected.
 */
export const L3_HICACHE_STORAGE_BACKEND_FLAG = "hicache-storage-backend";

/**
 * L3 KV stores an engine config can select (`l3KvBackend`; null = no L3).
 * `file` is SGLang's file store on the tower's L3 volume (SGLang only);
 * `mooncake` is the tower's Mooncake store (SGLang or vLLM).
 */
export const L3_KV_BACKENDS = ["file", "mooncake"] as const;
export const L3KvBackendSchema = z.enum(L3_KV_BACKENDS);
export type L3KvBackend = z.infer<typeof L3KvBackendSchema>;

/** The engines each L3 KV store runs on. */
export const L3_KV_BACKEND_ENGINES: Record<L3KvBackend, readonly string[]> = {
  file: ["sglang"],
  mooncake: ["sglang", "vllm"],
};

/** SGLang's host-memory (L2) HiCache flag, which the L3 storage tier extends and requires. */
export const HIERARCHICAL_CACHE_FLAG = "enable-hierarchical-cache";

/**
 * Health tag a k8s control tower sets to its nodes' shared
 * `topology.kubernetes.io/region` (e.g. `US-EAST-13`); absent when the nodes
 * span regions.
 */
export const CONTROL_TOWER_REGION_TAG = "region";

export type DefaultTrtFlags = {
  port: string;
  host: string;
  max_seq_len: string;
  max_batch_size: string;
  tp_size: string;
};

export type ConfigurableTrtFlags = Partial<{
  backend: "tensorrt" | "pytorch";
  // Parallelism
  tp_size: string;
  pp_size: string;
  cp_size: string;
  ep_size: string;
  cluster_size: string;
  gpus_per_node: string;

  // Resource limits
  kv_cache_free_gpu_memory_fraction: string;
  max_num_tokens: string;
  max_beam_width: string;

  max_batch_size: string;

  // Boolean flag (no value)
  trust_remote_code: "";
  log_level: string;
  num_postprocess_workers: string;

  // Tooling, reasoning, and templating
  reasoning_parser: string;

  // Advanced configuration
  extra_llm_api_options: string;
  config: string;
  tokenizer: string;
}>;

export const TRT_ATTENTION_BACKENDS = [
  "TRTLLM",
  "VANILLA",
  "FLASHINFER",
  "FLASHINFER_STAR_ATTENTION",
] as const;

export const TRT_CAPACITY_SCHEDULER_POLICIES = ["GUARANTEED_NO_EVICT"] as const;

export const TRT_CONTEXT_CHUNKING_POLICIES = ["EQUAL_PROGRESS"] as const;

export const TRT_GUIDED_DECODING_BACKENDS = ["xgrammar", "llguidance"] as const;

export type ConfigurableTrtConfigFileFlags = Partial<{
  scheduler_config: Partial<{
    capacity_scheduler_policy: (typeof TRT_CAPACITY_SCHEDULER_POLICIES)[number];
    context_chunking_policy: (typeof TRT_CONTEXT_CHUNKING_POLICIES)[number];
  }>;
  kv_cache_config: Partial<{
    free_gpu_memory_fraction: number;
    dtype: string;
    enable_block_reuse: boolean;
  }>;
  cuda_graph_config: Partial<{
    enable_padding: boolean;
    max_batch_size: number;
    batch_sizes: number[];
  }>;
  moe_config: Partial<{
    backend: string;
  }>;
  torch_compile_config: Partial<{
    enable_fullgraph: boolean;
    enable_piecewise_cuda_graph: boolean;
  }>;
  attn_backend: (typeof TRT_ATTENTION_BACKENDS)[number];
  enable_autotuner: boolean;
  speculative_config: Partial<{
    decoding_type: string;
    num_nextn_predict_layers: number;
  }>;
  trust_remote_code: boolean;
  return_perf_metrics: boolean;
  perf_metrics_max_requests: number;
  stream_interval: number;
  disable_overlap_scheduler: boolean;
  enable_chunked_prefill: boolean;
  guided_decoding_backend: (typeof TRT_GUIDED_DECODING_BACKENDS)[number];
  max_input_len: number;
}>;

/**
 * The mock engine is a real sidecar binary (`tooling/mock-engine`), so it takes
 * the same injected flags every other engine does: the control plane picks the
 * port/host it binds and the name it serves the deployment under.
 */
export type DefaultMockedFlags = {
  port: string;
  host: string;
  "served-model-name": string;
};

/**
 * Response-shape knobs an admin sets on the engine config. Every one of these
 * has a default inside the mock-engine binary, so an unset flag is simply not
 * passed rather than resolved here.
 */
export type ConfigurableMockedFlags = Partial<{
  "tokens-per-second": string;
  "tokens-per-request": string;
  jitter: string;
  "startup-delay-ms": string;
}>;

// ============= Environment Variables =============

type EnvironmentVariables<Engine extends InferenceEngine> =
  Engine extends "vllm"
    ? VllmEnvironmentVariables
    : Engine extends "sglang"
      ? SglangEnvironmentVariables
      : Engine extends "trt"
        ? TrtEnvironmentVariables
        : Engine extends "mocked"
          ? MockedEnvironmentVariables
          : never;

// No default environment variables for vllm and sglang
export type VllmEnvironmentVariables = ConfigurableVllmEnvironmentVariables;

export type SglangEnvironmentVariables = ConfigurableSglangEnvironmentVariables;

export type TrtEnvironmentVariables = ConfigurableTrtEnvironmentVariables;

/**
 * The mock engine reads no environment of its own — every knob is a flag — but
 * an instance still carries whatever the engine config sets, so this mirrors
 * what `MockedModelConfig` accepts.
 */
export type MockedEnvironmentVariables = ConfigurableVllmEnvironmentVariables;

export type ConfigurableVllmEnvironmentVariables = Partial<{
  NCCL_IGNORE_DISABLED_P2P: "1";
  VLLM_IMAGE_FETCH_TIMEOUT: string; // Timeout in seconds for fetching images from URLs (default: 5)
  MAX_JOBS: string; // Caps parallel JIT compile jobs (~3GB RAM each) when FlashInfer falls back to nvcc
}>;

export type ConfigurableSglangEnvironmentVariables = Partial<{
  NCCL_IGNORE_DISABLED_P2P: "1";
  NCCL_SHM_DISABLE: "1";
  NCCL_DEBUG: "INFO";
  SGL_ENABLE_JIT_DEEPGEMM: "1";
  CUDA_DEVICE_ORDER: "PCI_BUS_ID";
  PYTORCH_NVML_BASED_CUDA_CHECK: "1";
}>;

export type ConfigurableTrtEnvironmentVariables = Partial<{
  NCCL_IGNORE_DISABLED_P2P: "1";
  NCCL_SHM_DISABLE: "1";
  NCCL_DEBUG: "INFO";
  CUDA_DEVICE_ORDER: "PCI_BUS_ID";
  PYTORCH_NVML_BASED_CUDA_CHECK: "1";
  OMPI_MCA_btl: string;
  OMPI_MCA_btl_tcp_if_include: string;
}>;

// ============= Type guards =============

export const isSglangInferenceConfig = (
  config: InferenceEngineConfig<InferenceEngine>,
): config is InferenceEngineConfig<"sglang"> => {
  return config.engine === "sglang";
};

export const isVllmInferenceConfig = (
  config: InferenceEngineConfig<InferenceEngine>,
): config is InferenceEngineConfig<"vllm"> => {
  return config.engine === "vllm";
};

export const isMockedInferenceConfig = (
  config: InferenceEngineConfig<InferenceEngine>,
): config is InferenceEngineConfig<"mocked"> => {
  return config.engine === "mocked";
};

export const isTrtInferenceConfig = (
  config: InferenceEngineConfig<InferenceEngine>,
): config is InferenceEngineConfig<"trt"> => {
  return config.engine === "trt";
};
